import styled from "@emotion/styled";
import {
  Menu,
  MenuItem,
  Popover,
  Button as AriaButton
} from "react-aria-components";
import { motion } from "framer-motion";

export const Backdrop = styled(motion.div)`
  label: DropdownActionSheetBackdrop;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
`;

export const InvisibleTrigger = styled(AriaButton)`
  label: DropdownActionSheetTrigger;
  position: fixed;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
`;

export const StyledPopover = styled(Popover)`
  label: DropdownActionSheetPopover;
  background:
    radial-gradient(
      circle at top left,
      rgba(255, 255, 255, 0.25),
      rgba(255, 255, 255, 0.1)
    ),
    linear-gradient(
      to right,
      rgba(255, 255, 255, 0.06),
      rgba(255, 255, 255, 0.17)
    );
  backdrop-filter: blur(12px);
  border-radius: 1.6rem;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.12),
    0 8px 32px rgba(0, 0, 0, 0.08),
    inset 9px 9px 9px rgba(0, 0, 0, 0.015),
    inset 1px 1px 1px rgba(255, 255, 255, 0.9),
    inset -1px -1px 1px rgba(255, 255, 255, 0.6),
    inset -9px -9px 9px rgba(255, 255, 255, 0.08);
  overflow: hidden;
  z-index: 10000;
  touch-action: manipulation;
  -webkit-overflow-scrolling: touch;

  &[data-entering] {
    animation: popoverScaleIn 220ms cubic-bezier(0.32, 0.72, 0, 1);
  }

  &[data-exiting] {
    animation: popoverScaleOut 150ms cubic-bezier(0.32, 0.72, 0, 1);
  }

  @keyframes popoverScaleIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes popoverScaleOut {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(0.9);
    }
  }
`;

export const PopoverContent = styled.div`
  label: DropdownActionSheetContent;
  min-width: 220px;
  max-width: 300px;
  max-height: calc(
    100vh - 2rem - env(safe-area-inset-top) - env(safe-area-inset-bottom)
  );
  display: flex;
  flex-direction: column;

  @media (pointer: coarse) {
    max-width: calc(
      100vw - 2rem - env(safe-area-inset-left) - env(safe-area-inset-right)
    );
  }
`;

export const MenuWrapper = styled.div`
  label: DropdownActionSheetMenuWrapper;
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.15) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 2px;
  }
`;

export const FloatingHighlight = styled.div`
  label: DropdownActionSheetHighlight;
  position: absolute;
  left: 0.5rem;
  right: 0.5rem;
  top: 0;
  height: 0;
  opacity: 0;
  border-radius: 1.6rem;
  background: rgba(0, 0, 0, 0.06);
  pointer-events: none;
  z-index: 0;
  transform: scale(1);
  transition: transform 200ms ease-out;

  @media (pointer: fine) {
    &[data-pressed="true"] {
      transform: scale(0.97);
    }
  }
`;

export const StyledMenu = styled(Menu)`
  label: DropdownActionSheetMenu;
  position: relative;
  outline: none;
  padding: 0.8rem 0;
  z-index: 1;
`;

export const MenuHeader = styled.div`
  label: DropdownActionSheetHeader;
  padding: 0.9rem 1.6rem 0.8rem 1rem;
  text-align: left;
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`;

export const MenuTitle = styled.div`
  label: DropdownActionSheetTitle;
  font-size: 1.1rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

export const MenuMessage = styled.div`
  label: DropdownActionSheetMessage;
  font-size: 0.8rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.8);
  line-height: 1.4;
  margin-top: 0.025rem;
`;

export const IconWrapper = styled.span`
  label: DropdownActionSheetIcon;
  display: flex;
  align-items: center;
  justify-content: center;
  --icon-size-default: 24px;
  width: var(--icon-size, var(--icon-size-default));
  height: var(--icon-size, var(--icon-size-default));
  flex-shrink: 0;
  color: inherit;

  svg {
    display: block;
    width: calc(var(--icon-size, var(--icon-size-default)) * 0.9);
    height: calc(var(--icon-size, var(--icon-size-default)) * 0.9);
  }
`;

export const StyledMenuItem = styled(MenuItem)`
  label: DropdownActionSheetItem;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 1rem 0.65rem 1.2rem;
  cursor: pointer;
  outline: none;
  border: none;
  background: transparent;
  font-size: 1.1rem;
  font-weight: 400;
  text-align: left;
  width: 100%;
  letter-spacing: -0.02em;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  box-sizing: border-box;

  &[data-style="default"] {
    color: rgba(0, 0, 0, 0.85);
  }

  &[data-style="destructive"] {
    color: #ff3b30;
  }

  &[data-style="cancel"] {
    color: rgba(0, 0, 0, 0.85);
    font-weight: 600;
  }

  &[data-disabled] {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (pointer: coarse) {
    padding: 0.8rem 1rem;
  }

  strong,
  b {
    font-weight: 500;
  }
`;

export const ItemTitle = styled.span`
  padding: 0 0.6rem 0 0;
  transform: translateY(0.065em);
`;

export const LoadingContainer = styled.div`
  label: DropdownActionSheetLoading;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 2rem;
  min-width: 80px;
  min-height: 140px;
`;

export const LoadingSpinner = styled.div`
  label: DropdownActionSheetSpinner;
  width: 24px;
  height: 24px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
